import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useParams } from 'react-router-dom'
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers, faTasks, faTachometer, faEye,  faPencil, faArrowLeft, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { useRecoilState } from 'recoil';

import { USER_TRAIL_STATUS_OPTIONS } from "../../Constants/FieldOptions";
 
import { putUserUpdateAPI, getUserDetailAPI } from "../../API/User";
import FormErrorBox from "../Reusable/FormErrorBox";
import FormInputField from "../Reusable/FormInputField";
 
import FormUnixDateField from "../Reusable/FormUnixDateField";
import FormSelectField from "../Reusable/FormSelectField";
import PageLoadingContent from "../Reusable/PageLoadingContent";
import { topAlertMessageState, topAlertStatusState, userDetailState } from "../../AppState";


function UserUpdate() {
    ////
    //// URL Parameters.
    ////

    const { id } = useParams()

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);
    const [user, setUser] = useRecoilState(userDetailState);

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [forceURL, setForceURL] = useState("");
    const [isFetching, setFetching] = useState(false);
    const [birthday, setBirthday] = useState(null);
    const [clinicCode, setClinicCode] = useState(null);
    const [stripeCheckoutIntent, setStripeCheckoutIntent] = useState("");
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [injuryDate, setInjuryDate] = useState(null);
    const [trialStatus, setTrialStatus] = useState("");

    ////
    //// API.
    ////

    function onUserDetailSuccess(response){
        console.log("onUserDetailSuccess: Starting...");
        setUser(response);
        setBirthday(response.birthday);
        setClinicCode(response.clinicCode);
        setStripeCheckoutIntent(response.stripeCheckoutIntent);
        setEmail(response.email);
        setFirstName(response.firstName);
        setLastName(response.lastName);
        setInjuryDate(response.injuryDate);
        setTrialStatus(response.trialStatus);
    }

    function onUserDetailError(apiErr) {
        console.log("onUserDetailError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onUserDetailDone() {
        console.log("onUserDetailDone: Starting...");
        setFetching(false);
    }

    function onUserUpdateSuccess(response){
        console.log("onUserUpdateSuccess: Starting..."); // For debugging purposes only.

        // Update notification.
        setTopAlertStatus("success");
        setTopAlertMessage("User updated");
        setTimeout(() => {
            console.log("onUserUpdateSuccess: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Fetch again an updated list.
        setForceURL("/user/" + response.id);
    }

    function onUserUpdateError(apiErr) {
        console.log("onUserUpdateError: Starting..."); // For debugging purposes only.
        setErrors(apiErr);

        // Update notification.
        setTopAlertStatus("danger");
        setTopAlertMessage("Failed deleting");
        setTimeout(() => {
            console.log("onUserUpdateError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onUserUpdateDone() {
        console.log("onUserUpdateDone: Starting...");
        setFetching(false);
    }

    ////
    //// Event handling.
    ////

    const onSubmitClick = (e) => {
        console.log("onSubmitClick: Beginning...");
        setErrors({});
        setFetching(true);

        // To Snake-case for API from camel-case in React.
        const postData = {
            id: id,
            birthday: parseInt(birthday),
            clinic_code: clinicCode,
            email: email,
            first_name: firstName,
            injury_date: parseInt(injuryDate),
            last_name: lastName,
            trial_status: trialStatus,
        };
        // Update the user.
        console.log("onSubmitClick, user:", user);
        putUserUpdateAPI(postData, onUserUpdateSuccess, onUserUpdateError, onUserUpdateDone);
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.

            setFetching(true);
            getUserDetailAPI(
                id,
                onUserDetailSuccess,
                onUserDetailError,
                onUserDetailDone
            );
        }

        return () => { mounted = false; }
    }, [id]);

    ////
    //// Component rendering.
    ////

    if (forceURL !== "") {
        return <Navigate to={forceURL}  />
    }

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/users" aria-current="page"><FontAwesomeIcon className="fas" icon={faUsers} />&nbsp;Users</Link></li>
                            <li class=""><Link to={`/user/${id}`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Detail</Link></li>
                            <li class="is-active"><Link to={`/user`} aria-current="page"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/user/${id}`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Detail</Link></li>
                        </ul>
                    </nav>

                    {/* Modals */}
                    <nav class="box">
                        <p class="title is-4"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;User - {user.firstName} {user.lastName}</p>
                        {isFetching
                            ?
                            <PageLoadingContent displayMessage={"Please wait"} />
                            :
                            <>
                                <FormErrorBox errors={errors} />
                                <div class="columns">
                                    <div class="column">
                                        <p class="subtitle is-6"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</p>

                                        <FormUnixDateField
                                            label="Birthday (Optional)"
                                            name="birthday"
                                            placeholder="Text input"
                                            value={birthday}
                                            errorText={errors && errors.birthday}
                                            helpText=""
                                            onChange={(date)=>setBirthday(date)}
                                            isRequired={true}
                                            maxWidth="150px"
                                        />

                                        <FormInputField
                                            label="Clinic Code (Optional)"
                                            name="clinicCode"
                                            placeholder="Text input"
                                            value={user.clinicCode}
                                            errorText={errors && errors.clinicCode}
                                            helpText=""
                                            onChange={(e)=>setClinicCode(e.target.value)}
                                            isRequired={true}
                                            maxWidth="350px"
                                        />

                                        <FormInputField
                                            label="Stripe Checkout Intent"
                                            name="stripeCheckoutIntent"
                                            placeholder="Text input"
                                            value={stripeCheckoutIntent}
                                            errorText={errors && errors.clinicCode}
                                            helpText=""
                                            onChange={(e)=>setStripeCheckoutIntent(e.target.value)}
                                            maxWidth="350px"
                                            disabled={true}
                                        />

                                        <FormUnixDateField
                                            label="Created"
                                            name="created"
                                            placeholder="Text input"
                                            value={user.created}
                                            maxWidth="150px"
                                            disabled={true}
                                        />

                                        <FormInputField
                                            label="Email"
                                            name="email"
                                            placeholder="Text input"
                                            value={email}
                                            errorText={errors && errors.email}
                                            helpText=""
                                            onChange={(e)=>setEmail(e.target.value)}
                                            maxWidth="300px"
                                        />

                                        <FormInputField
                                            label="First Name"
                                            name="firstName"
                                            placeholder="Text input"
                                            value={firstName}
                                            errorText={errors && errors.firstName}
                                            helpText=""
                                            onChange={(e)=>setFirstName(e.target.value)}
                                            maxWidth="200px"
                                        />

                                        <FormInputField
                                            label="Last Name"
                                            name="lastName"
                                            placeholder="Text input"
                                            value={lastName}
                                            errorText={errors && errors.lastName}
                                            helpText=""
                                            onChange={(e)=>setLastName(e.target.value)}
                                            maxWidth="200px"
                                        />

                                        <FormUnixDateField
                                            label="Injury Date"
                                            name="injuryDate"
                                            placeholder="Text input"
                                            value={injuryDate}
                                            errorText={errors && errors.injuryDate}
                                            helpText=""
                                            onChange={(date)=>setInjuryDate(date)}
                                            isRequired={true}
                                            maxWidth="150px"
                                        />

                                        <FormUnixDateField
                                            label="Last Login Timestamp"
                                            name="lastLoginTimestamp"
                                            placeholder="Text input"
                                            value={user.lastLoginTimestamp}
                                            maxWidth="150px"
                                            disabled={true}
                                        />

                                        <FormUnixDateField
                                            label="Modified"
                                            name="modified"
                                            placeholder="Text input"
                                            value={user.modified}
                                            maxWidth="150px"
                                            disabled={true}
                                        />

                                        <FormSelectField
                                            label="Trial Status"
                                            name="Trial Status"
                                            placeholder="Text input"
                                            selectedValue={trialStatus}
                                            options={USER_TRAIL_STATUS_OPTIONS}
                                            errorText={errors && errors.trialStatus}
                                            helpText=""
                                            onChange={(e)=>setTrialStatus(e.target.value)}
                                            maxWidth="150px"
                                        />

                                        <div class="columns">
                                            <div class="column is-half">
                                                <Link to={`/user/${id}`} class="button is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Detail</Link>
                                            </div>
                                            <div class="column is-half has-text-right">
                                                <button onClick={onSubmitClick}  class="button is-primary is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faCheckCircle} />&nbsp;Save</button>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </>
                        }
                    </nav>
                </section>
            </div>
        </>
    );
}

export default UserUpdate;
